import { useCallback, useMemo } from 'react';
import { CookieService } from '@cx-web/feature-cookies';
import { buildBetSlipViewEvent, trackBetslipEvent } from '@cx-web/feature-tracking/betslip';
import { getNameFromPointSpreadValue, getPointSpreadValueFromName, isStrictlyUSHandicap } from '@cx-web/utils/us-utils';
import axios, { type AxiosResponse } from 'axios';

import { type Country, type Repub } from '@cx-modules/constants';
import { type StateCode } from '@cx-modules/types';

import LocalStorageService from '../localstorage-actions';
import { type Bet, type BetslipUtils, type TrackBet } from '../types';
import {
  calculateBookieOdds,
  calculateBookieOddsAndSelectedBookieOdds,
  calculateSelectedBookieOdds,
  getBestOdds,
  getBestOddsFromBet,
  getOdds,
  getSelectedBookieBetOdds,
} from '../utils/betslip-utils';

export const useBetslipUtils = (repub: Repub): BetslipUtils => {
  const cookieService = useMemo(() => new CookieService(), []);
  const localStorageService = useMemo(() => new LocalStorageService(), []);

  const getBetMeta = useCallback(
    (
      bet: Bet,
    ): {
      betId: number;
      betName: string;
      marketName: string;
      spread: string | number;
      subeventName: string;
    } => {
      const strictlyUS = isStrictlyUSHandicap(bet);

      const { betId, marketName, name, subeventName } = bet;
      const betName = (strictlyUS && getNameFromPointSpreadValue(name)) || name;
      const spread = (strictlyUS && getPointSpreadValueFromName(name)) || '';

      return {
        betId,
        betName,
        subeventName,
        marketName,
        spread,
      };
    },
    [],
  );

  const getBetslipAPI = useCallback(
    (
      betIds: number[],
      countryCode: Country,
      state: StateCode | '',
      completion: (success: any, res: any) => void,
    ): void => {
      axios({
        method: 'GET',
        url: `/api/cx/sports-catalogue/v1/betslip?betIds=${betIds}`,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Cache-Control': 'no-store, no-cache, must-revalidate, max-age=0',
          Expires: 'Thu, 01 Jan 1970 00:00:00 GMT',
          Pragma: 'no-cache',
          'subdivision-code': state,
          'country-code': countryCode,
          repub,
        },
      })
        .then((res: AxiosResponse) => {
          completion({ success: true }, res.data);
        })
        .catch((e: any) => {
          console.log(e);
          completion({ success: false }, e);
        });
    },
    [repub],
  );

  const getBetslipCookieArray = useCallback((): [string, string][] => {
    const betslipCookieArray = cookieService.select('intBetslip').getArray(',', (item: string) => {
      return item.split('~');
    });

    // Ensure we filter out non-tuple data
    return betslipCookieArray.filter(c => !!c?.[1]);
  }, [cookieService]);

  const getLocalStorageBetslip = useCallback(
    () => localStorageService.select('intBetslip').getJSON() || {},
    [localStorageService],
  );

  const getBetSelectedBookieFromLocalStore = useCallback(
    (betId: string): string | null => {
      const localStorageBetslip = getLocalStorageBetslip();

      if (typeof localStorageBetslip[betId] !== 'undefined') {
        return localStorageBetslip[betId].selectedBookie;
      }

      return null;
    },
    [getLocalStorageBetslip],
  );

  const trackBetSlip = useCallback((betSlip: TrackBet[], stake?: number): void => {
    // Payload shape lives in buildBetSlipViewEvent so the store-backed MiniBetslipFromStore can emit
    // the identical event without depending on this hook. See NGF-6313.
    trackBetslipEvent(buildBetSlipViewEvent(betSlip, stake));
  }, []);

  const postOddsClick = useCallback(
    (betId: number, bookmakerCode: string, countryCode: Country, state: StateCode | ''): void => {
      axios({
        method: 'POST',
        url: '/api/cx/sports-catalogue/v1/odds-click',
        headers: {
          'subdivision-code': state,
          'country-code': countryCode,
          repub,
        },
        data: { betId, bookmakerCode },
      }).catch(() => {});
    },
    [repub],
  );

  // Memoize the returned bag so betslip-context's callbacks (which take `betslipUtils` as a dep)
  // keep stable identities across unrelated re-renders. The imported helpers are module-scoped and
  // already stable; only the local closures need listing here.
  return useMemo(
    () => ({
      getBetMeta,
      getBetslipAPI,
      getBetslipCookieArray,
      getOdds,
      getSelectedBookieBetOdds,
      getBestOdds,
      getBestOddsFromBet,
      getLocalStorageBetslip,
      getBetSelectedBookieFromLocalStore,
      postOddsClick,
      trackBetSlip,
      calculateBookieOdds,
      calculateSelectedBookieOdds,
      calculateBookieOddsAndSelectedBookieOdds,
    }),
    [
      getBetMeta,
      getBetslipAPI,
      getBetslipCookieArray,
      getLocalStorageBetslip,
      getBetSelectedBookieFromLocalStore,
      postOddsClick,
      trackBetSlip,
    ],
  );
};
